import React, {useEffect, useState} from "react";
import styles, {C} from "../styles";
import {apiGetResults, apiVote} from "../api/surveys";
import useIsMobile from "../hooks/useIsMobile";

// Fonds pastel (boutons de vote, barres de résultat) et accents foncés assortis
// (texte des boutons, mise en avant de la réponse de l'agent) — même code couleur
// sémantique qu'avant (rouge doux -> vert/teal doux), juste désaturé.
const LIKERT_BG_COLORS = {
    "Pas du tout d'accord": "#F3D2CC",
    "Plutôt pas d'accord": "#F6E2C6",
    "Plutôt d'accord":     "#D7ECD9",
    "D'accord":            "#CFE8E4",
    "Tout à fait d'accord":"#BADBD6",
};

const LIKERT_ACCENT_COLORS = {
    "Pas du tout d'accord": "#A2412F",
    "Plutôt pas d'accord": "#9C6B22",
    "Plutôt d'accord":     "#3D7A4E",
    "D'accord":            "#0F6A71",
    "Tout à fait d'accord":"#0B4F54",
};

const DEFAULT_BG_COLOR = "#EAEAEA";
const DEFAULT_ACCENT_COLOR = "#666";

function getBgColor(answer) {
    return LIKERT_BG_COLORS[answer] || DEFAULT_BG_COLOR;
}

function getAccentColor(answer) {
    return LIKERT_ACCENT_COLORS[answer] || DEFAULT_ACCENT_COLOR;
}

export default function Survey({survey, user, answers}) {
    const [resultData, setResultData] = useState(null);
    const [voting, setVoting] = useState(false);
    const [error, setError] = useState("");
    const isMobile = useIsMobile();

    function loadResults() {
        apiGetResults(survey.id).then(setResultData);
    }

    useEffect(() => {
        loadResults();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function vote(answer) {
        if (!user || voting) return;
        setVoting(true);
        setError("");
        const data = await apiVote(survey.id, answer);
        if (data.error) setError(data.error);
        await loadResults();
        setVoting(false);
    }

    const hasVoted = resultData?.user_voted;
    const userAnswer = resultData?.user_answer;
    const locked = resultData?.locked;

    function getTotal() {
        if (!resultData || locked || !resultData.results) return 0;
        return resultData.results.reduce((sum, r) => sum + r.total, 0);
    }

    function getCount(answer) {
        if (!resultData || locked || !resultData.results) return 0;
        const item = resultData.results.find(r => r.answer === answer);
        return item ? item.total : 0;
    }

    function getPct(answer) {
        const total = getTotal();
        if (total === 0) return 0;
        return Math.round((getCount(answer) / total) * 100);
    }

    const surveyAnswers = answers && answers.length > 0 ? answers : Object.keys(LIKERT_BG_COLORS);

    return (
        <div style={{...styles.card, borderLeft: `4px solid ${C.primary}`}}>
            <div style={{display: "flex", alignItems: "center", gap: 8, marginBottom: 8}}>
                <span style={{fontSize: 20}}>📊</span>
                <strong style={{fontSize: 13, color: C.textMuted, textTransform: "uppercase", letterSpacing: 0.5}}>
                    Sondage Paroles d'Agents
                </strong>
            </div>
            <p style={{fontSize: 16, fontWeight: 600, marginBottom: 14, lineHeight: "22px"}}>
                {survey.question}
            </p>

            {/* Boutons de vote */}
            {user && !hasVoted && (
                <div style={{display: "flex", flexDirection: "column", gap: 8, marginBottom: 12}}>
                    {surveyAnswers.map(answer => (
                        <button
                            key={answer}
                            onClick={() => vote(answer)}
                            disabled={voting}
                            style={{
                                backgroundColor: getBgColor(answer),
                                color: getAccentColor(answer),
                                border: "none",
                                padding: isMobile ? "8px 12px" : "10px 16px",
                                borderRadius: isMobile ? 6 : 8,
                                cursor: voting ? "wait" : "pointer",
                                fontWeight: 600,
                                fontSize: isMobile ? 13 : 14,
                                textAlign: "left",
                                transition: "opacity 0.15s",
                                opacity: voting ? 0.7 : 1,
                            }}
                        >
                            {answer}
                        </button>
                    ))}
                </div>
            )}

            {/* Confirmation vote enregistré */}
            {hasVoted && userAnswer && (
                <div style={{
                    backgroundColor: C.primaryLight,
                    border: `1px solid ${C.primary}`,
                    borderRadius: 8,
                    padding: "8px 12px",
                    marginBottom: 12,
                    fontSize: 13,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                }}>
                    <span>✅</span>
                    <span>Vous avez voté : <strong style={{color: getAccentColor(userAnswer)}}>{userAnswer}</strong></span>
                </div>
            )}

            {error && <p style={{color: C.danger, fontSize: 13, marginBottom: 8}}>{error}</p>}

            {!user && (
                <p style={{fontSize: 13, color: C.textMuted, marginBottom: 8}}>
                    Connectez-vous pour participer à ce sondage.
                </p>
            )}

            {/* Résultats */}
            {resultData && locked && (
                <div style={styles.warning}>
                    <strong>Résultats masqués pour protéger l'anonymat.</strong>
                    <p style={{margin: "4px 0 0"}}>
                        {resultData.reason === "votes"
                            ? `${resultData.vote_count} participation(s) pour l'instant. Affichage à partir de ${resultData.minimum_votes} participations.`
                            : `Cette consultation concerne ${resultData.member_count} agent(s) inscrit(s). Affichage à partir de ${resultData.minimum_required} agents.`}
                    </p>
                </div>
            )}

            {resultData && !locked && hasVoted && (
                <div style={{marginTop: 8}}>
                    <p style={{fontSize: 12, color: C.textMuted, marginBottom: 8}}>
                        {getTotal()} participation(s)
                    </p>
                    {surveyAnswers.map(answer => {
                        const pct = getPct(answer);
                        const count = getCount(answer);
                        const isUserAnswer = answer === userAnswer;
                        return (
                            <div key={answer} style={{marginBottom: 8}}>
                                <div style={{display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 3}}>
                                    <span style={{fontWeight: isUserAnswer ? 700 : 400, color: isUserAnswer ? getAccentColor(answer) : C.text}}>
                                        {isUserAnswer ? "✓ " : ""}{answer}
                                    </span>
                                    <span style={{color: C.textMuted}}>{pct}% ({count})</span>
                                </div>
                                <div style={{
                                    backgroundColor: "#E8F4F1",
                                    height: 8,
                                    borderRadius: 4,
                                    overflow: "hidden",
                                }}>
                                    <div style={{
                                        width: `${pct}%`,
                                        backgroundColor: getAccentColor(answer),
                                        height: "100%",
                                        borderRadius: 4,
                                        transition: "width 0.5s ease",
                                    }}/>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
